import React, { useState, useEffect, useCallback } from 'react';
import {
  Typography,
  Tabs,
  List,
  Card,
  Tag,
  Button,
  Space,
  Spin,
  Empty,
  Row,
  Col,
  message,
  Avatar,
} from 'antd';
import { RightOutlined, ShoppingOutlined } from '@ant-design/icons';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import api from '../services/api';
import dayjs from 'dayjs';

const { Title, Text } = Typography;
const GOLD = '#b5892a';

const ORDER_STATUS_CONFIG_KEYS = {
  // 客户视角：pending_receipt (我们在等拍卖行发货) 和 ready_to_ship (拍卖行已发到我们这)
  // 在客户端统一显示为「待发货」，对客户透明
  // inspecting 是历史状态，也归为「待发货」
  pending_receipt: { labelKey: 'orders.statusShipping', color: 'cyan' },
  inspecting:      { labelKey: 'orders.statusShipping', color: 'cyan' },
  ready_to_ship:   { labelKey: 'orders.statusShipping', color: 'cyan' },
  shipping:        { labelKey: 'orders.statusShipping', color: 'geekblue' },
  delivered:       { labelKey: 'orders.statusDelivered', color: 'success' },
  cancelled:       { labelKey: 'orders.statusCancelled', color: 'default' },
  refunded:        { labelKey: 'orders.statusRefunded', color: 'default' },
};

const STATUS_TABS_KEYS = [
  { key: '', labelKey: 'common.all' },
  // 特殊 key: __unpaid__ → 扣款失败/待付款的订单（后端用 payment_pending=true 过滤）
  { key: '__unpaid__', labelKey: 'orders.statusPending' },
  // 「待发货」合并: pending_receipt（等拍卖行发到我们）+ ready_to_ship（已收货等发给客户）
  { key: 'pending_receipt,ready_to_ship', labelKey: 'orders.statusShipping' },
  { key: 'shipping', labelKey: 'orders.statusShipping' },
  { key: 'delivered', labelKey: 'orders.statusDelivered' },
  { key: 'cancelled', labelKey: 'orders.statusCancelled' },
];

function formatCNY(val) {
  if (val == null) return '—';
  return '¥' + Number(val).toFixed(2);
}

export default function Orders() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [orders, setOrders] = useState([]);
  const [loading, setLoading] = useState(false);
  // 初始 tab 可由 URL ?status= 指定（如「我的」页点「待支付/待发货」跳入）
  const [statusFilter, setStatusFilter] = useState(() => new URLSearchParams(window.location.search).get('status') || '');

  const fetchOrders = useCallback(async () => {
    setLoading(true);
    try {
      const params = {};
      if (statusFilter === '__unpaid__') {
        params.payment_pending = 'true';
      } else if (statusFilter) {
        params.status = statusFilter;
      }
      const { data } = await api.get('/orders', { params });
      setOrders(data.data || []);
    } catch (err) {
      message.error(t('common.failed'));
    } finally {
      setLoading(false);
    }
  }, [statusFilter]);

  useEffect(() => {
    fetchOrders();
  }, [fetchOrders]);

  const renderOrder = (order) => {
    // 扣款失败 → 显示「待付款」, 否则显示对应状态
    const isUnpaid = !!order.deduct_error && !order.deducted_at;
    const statusCfgRaw = isUnpaid
      ? { labelKey: 'orders.statusPending', color: 'red' }
      : (ORDER_STATUS_CONFIG_KEYS[order.status] || { labelKey: null, color: 'default' });
    const statusCfg = { label: statusCfgRaw.labelKey ? t(statusCfgRaw.labelKey) : order.status, color: statusCfgRaw.color };

    // 后端返回的字段是 image_urls / title_zh / title_ja（拼自 auction_lots 表）
    let img = null;
    try {
      const raw = order.image_urls || order.lot_image_urls;
      const urls = typeof raw === 'string' ? JSON.parse(raw) : raw;
      img = Array.isArray(urls) ? urls[0] : null;
    } catch (_) {}
    const lotTitle = order.title_zh || order.title_ja || order.lot_title || t('browse.title');

    return (
      <Card
        key={order.id}
        hoverable
        style={{ marginBottom: 16, borderRadius: 8, cursor: 'pointer' }}
        onClick={() => navigate(`/orders/${order.id}`)}
        bodyStyle={{ padding: '16px 20px' }}
      >
        <Row align="middle" gutter={16}>
          <Col flex="64px">
            <Avatar
              shape="square"
              size={64}
              src={img}
              style={{ background: '#f5f5f5', borderRadius: 6 }}
              icon={<ShoppingOutlined />}
            />
          </Col>
          <Col flex={1} style={{ minWidth: 0 }}>
            <Row justify="space-between" align="top" style={{ marginBottom: 6 }}>
              <Col>
                <Text
                  strong
                  style={{
                    display: 'block',
                    overflow: 'hidden',
                    textOverflow: 'ellipsis',
                    whiteSpace: 'nowrap',
                    maxWidth: 320,
                  }}
                  title={lotTitle}
                >
                  {lotTitle}
                </Text>
                <Text style={{ color: '#aaa', fontSize: 12 }}>{t('orders.orderNo')}：{order.order_no}</Text>
              </Col>
              <Col>
                <Tag color={statusCfg.color}>{statusCfg.label}</Tag>
              </Col>
            </Row>
            <Row justify="space-between" align="middle">
              <Col>
                <Space split={<span style={{ color: '#ddd' }}>·</span>}>
                  <Text style={{ color: '#888', fontSize: 13 }}>
                    {dayjs(order.created_at).format('YYYY-MM-DD HH:mm')}
                  </Text>
                  {order.house_name && (
                    <Text style={{ color: '#888', fontSize: 13 }}>{order.house_name}</Text>
                  )}
                </Space>
              </Col>
              <Col>
                <Space align="center">
                  <div style={{ textAlign: 'right' }}>
                    <Text style={{ color: '#888', fontSize: 12, display: 'block' }}>{t('orders.amount')}</Text>
                    <Text style={{ color: GOLD, fontWeight: 700, fontSize: 16 }}>
                      {formatCNY(order.total_amount_cny)} CNY
                    </Text>
                  </div>
                  <RightOutlined style={{ color: '#ccc' }} />
                </Space>
              </Col>
            </Row>
          </Col>
        </Row>
      </Card>
    );
  };

  return (
    <div style={{ padding: '24px 32px', maxWidth: 1200, margin: '0 auto' }}>
      <Title level={3} style={{ marginBottom: 24 }}>{t('orders.title')}</Title>

      <div
        style={{
          background: '#fff',
          borderRadius: 8,
          boxShadow: '0 2px 8px rgba(0,0,0,0.06)',
          overflow: 'hidden',
          marginBottom: 16,
        }}
      >
        <Tabs
          activeKey={statusFilter}
          onChange={(key) => setStatusFilter(key)}
          style={{ padding: '0 20px', borderBottom: '1px solid #f0f0f0' }}
          items={STATUS_TABS_KEYS.map((tab) => ({ key: tab.key, label: t(tab.labelKey) }))}
        />
      </div>

      <Spin spinning={loading}>
        {orders.length === 0 && !loading ? (
          <Empty description={t('orders.empty')} style={{ padding: '60px 0' }} />
        ) : (
          orders.map((order) => renderOrder(order))
        )}
      </Spin>
    </div>
  );
}
